import { useEffect } from "react";
import {
  isLocalSnapshotId,
  localApiRoutes,
  RivetsApiError,
  type DirectoryListingResponse,
  type FileContentResponse,
  type LocalCancelResponse,
  type LocalPreviewsResponse,
  type LoopbackTransportDescriptor,
  type SnapshotDiffResponse,
  type SnapshotDiffScope,
  type SnapshotSummary,
  type WorkerInfo,
  type Worktree,
  type WorktreeDetailResponse,
} from "@rivets/core";
import type { QueryClient } from "@tanstack/react-query";
import { client } from "./api";
import { queryClient as appQueryClient } from "./query-client";
import { qk } from "./query-keys";
import { forgetLocalApiRefusals, forgetUnreachableLocalApis, isLocalApiUnavailable, LocalApiSession, LocalUnavailableError, type LocalApiDeps } from "./localApi";
import { useChatStore } from "@/features/chat/chatStore";
import { applyLocalSummary } from "@/features/worktree/snapshotCache";
import { useConnectionStatus } from "./connectionState";

/**
 * Per-worktree data source: **local** when the worktree's worker advertises a
 * running LocalApi on its loopback listener and this browser can reach it
 * (socket-first host matching), otherwise **relay**. Every read below tries
 * local first when a ready session covers the worktree and falls back to the
 * relay on any failure, writing into the same React Query keys, so a fallback
 * is invisible to the view reading them.
 *
 * Routed local: directory listings, file content, the snapshot summary, per
 * file diffs, the worktree watch (which replaces snapshot refetches), the
 * provisional chat tail, the cancel hint, and preview service URLs.
 */

let deps: LocalApiDeps = { api: client };
let cache: QueryClient = appQueryClient;
const sessions = new Map<string, LocalApiSession>();
/** Provisional expiry sweep, running only while any provisional exists. */
let sweepTimer: ReturnType<typeof setInterval> | null = null;
const PROVISIONAL_SWEEP_MS = 5_000;

/** Test seam. */
export function configureDataSource(next: { deps?: LocalApiDeps; queryClient?: QueryClient; reconnectBaseMs?: number }): void {
  if (next.deps) deps = next.deps;
  if (next.reconnectBaseMs !== undefined) reconnectBaseMs = next.reconnectBaseMs;
  if (next.queryClient) cache = next.queryClient;
}

export function resetDataSource(): void {
  for (const session of sessions.values()) session.close();
  sessions.clear();
  for (const state of reconnect.values()) if (state.timer) clearTimeout(state.timer);
  reconnect.clear();
  watchers.clear();
  chatHolds.clear();
  if (sweepTimer) clearInterval(sweepTimer);
  sweepTimer = null;
}

function cachedWorktree(worktreeId: string): Worktree | undefined {
  const detail = cache.getQueryData<WorktreeDetailResponse>(qk.worktree(worktreeId))?.worktree;
  if (detail) return detail;
  for (const [, data] of cache.getQueriesData<{ worktrees: Worktree[] }>({ queryKey: ["worktrees"] })) {
    const found = data?.worktrees.find((worktree) => worktree.id === worktreeId);
    if (found) return found;
  }
  return undefined;
}

function cachedWorker(orgId: string, workerId: string): WorkerInfo | undefined {
  return cache.getQueryData<{ workers: WorkerInfo[] }>(qk.workers(orgId))?.workers.find((worker) => worker.id === workerId);
}

type RunningLoopback = Extract<LoopbackTransportDescriptor, { state: "running" }>;

/** The worker's running LocalApi advertisement, when it has one. */
export function localApiDescriptor(worker: Pick<WorkerInfo, "status" | "capabilities"> | undefined): RunningLoopback | null {
  const loopback = worker?.capabilities?.loopbackTransport;
  if (worker?.status !== "online" || loopback?.state !== "running" || !loopback.localApi) return null;
  return loopback;
}

/** After a session closes (sleep, daemon restart, token expiry, a handshake
 * that timed out), the next session is tried after this, doubling per failure
 * up to the cap: 5 s → 60 s (CONTRACT C5). */
export const LOCAL_RECONNECT_BASE_MS = 5_000;
let reconnectBaseMs = LOCAL_RECONNECT_BASE_MS;
export const LOCAL_RECONNECT_MAX_MS = 60_000;
const reconnect = new Map<string, { attempt: number; notBefore: number; timer?: ReturnType<typeof setTimeout> }>();

function handlersFor(workerId: string) {
  return {
    onWorktreeChanged: (worktreeId: string, summary: SnapshotSummary) => applyLocalSummary(cache, worktreeId, summary),
    onProvisional: (chatId: string, events: import("@rivets/core").ProvisionalChatEvent[]) => {
      useChatStore.getState().applyProvisional(chatId, events);
      ensureSweep();
    },
    // Nothing to reread on ready: a watch answers at once with the current
    // local summary, which replaces the relay's through `onWorktreeChanged`.
    onReady: () => {
      const state = reconnect.get(workerId);
      if (state?.timer) clearTimeout(state.timer);
      reconnect.delete(workerId);
    },
    onClosed: (_workerId: string, { negative }: { negative: boolean; wasReady: boolean }) => {
      // Provisionals never outlive the socket that delivered them — this
      // worker's socket; another worker's chats keep theirs.
      for (const [chatId, hold] of chatHolds) {
        if (cachedWorktree(hold.worktreeId)?.workerId === workerId) useChatStore.getState().dropProvisional(chatId);
      }
      refreshWatchedSummaries(workerId);
      if (!negative) scheduleReconnect(workerId);
    },
  };
}

function scheduleReconnect(workerId: string, immediate = false) {
  const state = reconnect.get(workerId) ?? { attempt: 0, notBefore: 0 };
  if (state.timer) clearTimeout(state.timer);
  const delay = immediate ? 0 : Math.min(LOCAL_RECONNECT_MAX_MS, reconnectBaseMs * 2 ** state.attempt);
  state.attempt += immediate ? 0 : 1;
  state.notBefore = Date.now() + delay;
  // Something on screen still wants this worker local: come back on our own,
  // rather than waiting for the next read to notice.
  state.timer = setTimeout(() => {
    state.timer = undefined;
    const orgId = orgOfWorker(workerId);
    if (orgId && wantsLocal(workerId)) sessionForWorker(orgId, workerId);
  }, delay);
  reconnect.set(workerId, state);
}

function wantsLocal(workerId: string): boolean {
  for (const holder of watchers.values()) if (holder.workerId === workerId) return true;
  for (const hold of chatHolds.values()) if (cachedWorktree(hold.worktreeId)?.workerId === workerId) return true;
  return false;
}

function orgOfWorker(workerId: string): string | undefined {
  for (const holder of watchers.keys()) {
    const worktree = cachedWorktree(holder);
    if (worktree?.workerId === workerId) return worktree.orgId;
  }
  for (const hold of chatHolds.values()) {
    const worktree = cachedWorktree(hold.worktreeId);
    if (worktree?.workerId === workerId) return worktree.orgId;
  }
  return undefined;
}

/** Waking from sleep or coming back online: a worker whose session closed
 * meanwhile is tried again at once, not after its backoff, and a listener
 * that did not answer is worth asking again. */
export function reconnectLocalNow(): void {
  forgetUnreachableLocalApis();
  for (const workerId of reconnect.keys()) {
    const state = reconnect.get(workerId);
    if (state) state.attempt = 0;
    scheduleReconnect(workerId, true);
  }
}

/** The relay says the worker is online again — typically a restart, perhaps
 * on a new port: its fast path is tried now, whatever it refused before. It
 * used to stay on the relay until another workspace was opened (native #5,
 * web #15). */
export function workerCameOnline(workerId: string): void {
  forgetLocalApiRefusals(workerId);
  const existing = sessions.get(workerId);
  if (existing && existing.state !== "closed") return;
  if (!wantsLocal(workerId)) return;
  const state = reconnect.get(workerId);
  if (state) state.attempt = 0;
  scheduleReconnect(workerId, true);
}

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("online", reconnectLocalNow);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") reconnectLocalNow(); });
  }
}

/** Watched worktrees switch source: their summaries (and the `lsnap_` ids
 * inside them) are reread from whichever side now serves them. */
function refreshWatchedSummaries(workerId: string) {
  for (const [worktreeId, holder] of watchers) {
    if (holder.workerId !== workerId) continue;
    void cache.invalidateQueries({ queryKey: qk.snapshot(worktreeId), exact: true });
  }
}

function ensureSweep() {
  if (sweepTimer) return;
  sweepTimer = setInterval(() => {
    const store = useChatStore.getState();
    // The TTL runs only while the relay could confirm them (web #10).
    store.expireProvisional(Date.now(), useConnectionStatus.getState().relayOpenSince);
    const anyLeft = Object.values(useChatStore.getState().chats).some((stream) => stream.provisional.length > 0);
    if (!anyLeft && sweepTimer) {
      clearInterval(sweepTimer);
      sweepTimer = null;
    }
  }, PROVISIONAL_SWEEP_MS);
}

/**
 * The session for a worktree's worker: started on first use, `null` when the
 * worker has no LocalApi or it already failed this page session.
 */
export function sessionForWorktree(worktreeId: string): LocalApiSession | null {
  const worktree = cachedWorktree(worktreeId);
  if (!worktree?.workerId) return null;
  return sessionForWorker(worktree.orgId, worktree.workerId);
}

function sessionForWorker(orgId: string, workerId: string): LocalApiSession | null {
  const existing = sessions.get(workerId);
  if (existing && existing.state !== "closed") return existing;
  const descriptor = localApiDescriptor(cachedWorker(orgId, workerId));
  if (!descriptor || isLocalApiUnavailable(workerId, descriptor.endpoint)) return null;
  // A session that was up and closed is retried, but not before its backoff.
  const backoff = reconnect.get(workerId);
  if (existing && backoff && Date.now() < backoff.notBefore) return null;
  const session = new LocalApiSession(workerId, descriptor, deps, handlersFor(workerId));
  sessions.set(workerId, session);
  // Worktrees watched and chats subscribed before this session existed (a
  // chat opened before the worker list arrived, or across a reconnect)
  // follow it now.
  for (const [worktreeId, holder] of watchers) {
    if (holder.workerId === workerId) session.watchWorktree(worktreeId);
  }
  for (const [chatId, hold] of chatHolds) {
    if (cachedWorktree(hold.worktreeId)?.workerId === workerId) session.subscribeChat(chatId);
  }
  void session.start();
  return session;
}

/** A session ready to serve this worktree, or null (read the relay). */
export function readyLocalSession(worktreeId: string): LocalApiSession | null {
  const session = sessionForWorktree(worktreeId);
  if (!session) return null;
  if (session.covers(worktreeId)) return session;
  // Ready but not covering it (a task created after the grant was minted):
  // one renewal is asked for; this read goes to the relay meanwhile.
  session.refreshGrantFor(worktreeId);
  return null;
}

function signalAborted(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

async function viaLocal<T>(worktreeId: string, local: (session: LocalApiSession) => Promise<T>, relay: () => Promise<T>): Promise<T> {
  const session = readyLocalSession(worktreeId);
  if (session) {
    try {
      return await local(session);
    } catch (error) {
      if (signalAborted(error)) throw error;
      // A vanished local snapshot is the caller's to act on. Anything else —
      // an unavailable session, or an error answer to this one request (a
      // rejected path, a missing file, a busy daemon) — is the relay's to
      // answer, and the session stays up for the next read.
      if (error instanceof RivetsApiError && error.status === 410) throw error;
    }
  }
  return relay();
}

export function readDirectory(worktreeId: string, path: string, signal?: AbortSignal): Promise<DirectoryListingResponse> {
  return viaLocal(
    worktreeId,
    (session) => session.request(worktreeId, localApiRoutes.dir(worktreeId, path), { signal }),
    () => client.withSignal(signal).worktreeDirectory(worktreeId, path),
  );
}

export function readFile(worktreeId: string, path: string, signal?: AbortSignal): Promise<FileContentResponse> {
  return viaLocal(
    worktreeId,
    (session) => session.request(worktreeId, localApiRoutes.file(worktreeId, path), { signal }),
    () => client.withSignal(signal).worktreeFile(worktreeId, path),
  );
}

/** Saves an edited file. Always through the relay: it holds the worktree
 * ownership check and has the worker publish the snapshot that lists the edit.
 * The saved text becomes the cached copy at once, so the viewer never shows
 * the file from before the save while a re-read is on its way. */
export async function writeFile(
  worktreeId: string,
  path: string,
  content: string,
  expectedHash: string | null,
  /** False once the session the save began in is gone: its result then
   * touches nothing the next account will read. */
  stillCurrent: () => boolean = () => true,
): Promise<void> {
  await client.writeWorktreeFile(worktreeId, { path, content, expectedHash });
  if (!stillCurrent()) return;
  // A read still in flight began before the save and would land the old text
  // over the new; it is dropped rather than raced.
  await cache.cancelQueries({ queryKey: qk.file(worktreeId, path), exact: true });
  cache.setQueryData<FileContentResponse>(qk.file(worktreeId, path), { path, content, binary: false });
}

/** Drops the cached copy of a file so its viewer reads it again. */
export function rereadFile(worktreeId: string, path: string): void {
  void cache.invalidateQueries({ queryKey: qk.file(worktreeId, path) });
}

export function readSnapshotSummary(worktreeId: string, signal?: AbortSignal): Promise<SnapshotSummary | null> {
  return viaLocal(
    worktreeId,
    (session) => session.request<SnapshotSummary>(worktreeId, localApiRoutes.snapshot(worktreeId), { signal }),
    () => client.withSignal(signal).worktreeSnapshotSummary(worktreeId),
  );
}

/** A per-file diff. A local (`lsnap_`) snapshot can only be read locally; if
 * the session is gone it answers as a vanished snapshot, which makes the
 * caller reload the summary — from the relay, now. */
export async function readSnapshotDiff(
  worktreeId: string,
  snapshotId: string,
  path: string,
  scope: SnapshotDiffScope,
  signal?: AbortSignal,
): Promise<SnapshotDiffResponse> {
  if (isLocalSnapshotId(snapshotId)) {
    const session = readyLocalSession(worktreeId);
    if (!session) throw new RivetsApiError(410, "The local snapshot is no longer available.", "snapshot_gone");
    try {
      return await session.request(worktreeId, localApiRoutes.diff(worktreeId, { snapshotId, path, scope }), { signal });
    } catch (error) {
      if (error instanceof LocalUnavailableError) throw new RivetsApiError(410, "The local snapshot is no longer available.", "snapshot_gone");
      throw error;
    }
  }
  return client.withSignal(signal).snapshotDiff(snapshotId, path, scope);
}

/** Preview services with a direct loopback URL when served locally. */
export async function listLocalPreviews(worktreeId: string): Promise<Array<LocalPreviewsResponse["services"][number] & { directUrl: string }> | null> {
  const session = readyLocalSession(worktreeId);
  if (!session) return null;
  try {
    const response = await session.request<LocalPreviewsResponse>(worktreeId, localApiRoutes.previews(worktreeId));
    return response.services.map((service) => ({
      ...service,
      directUrl: `${service.protocol}://127.0.0.1:${service.port}${service.basePath}`,
    }));
  } catch {
    return null;
  }
}

/** The fast-path cancel: the daemon stops the job at once when it runs it.
 * It is only a hint — the relay cancel for the same job is always sent too,
 * and the ordinary `job.update canceled` is what the transcript shows. */
export function sendLocalCancelHint(worktreeId: string, chatId: string, jobId: string): void {
  const session = readyLocalSession(worktreeId);
  if (!session?.canWrite) return;
  void session.request<LocalCancelResponse>(worktreeId, localApiRoutes.cancel(chatId), { method: "POST", body: { jobId } }).catch(() => undefined);
}

// -- watches and provisional tails ---------------------------------------------

const watchers = new Map<string, { workerId: string; count: number }>();
const chatHolds = new Map<string, { worktreeId: string; count: number }>();

/** Whether the daemon's watch currently owns this worktree's summary. */
export function isLocallyWatched(worktreeId: string): boolean {
  const holder = watchers.get(worktreeId);
  if (!holder) return false;
  return sessions.get(holder.workerId)?.isWatching(worktreeId) ?? false;
}

/** Watch a worktree over LocalApi while held. */
export function holdLocalWorktree(worktreeId: string): () => void {
  const worktree = cachedWorktree(worktreeId);
  const session = worktree?.workerId ? sessionForWorker(worktree.orgId, worktree.workerId) : null;
  if (!session || !worktree?.workerId) return () => undefined;
  const holder = watchers.get(worktreeId);
  if (holder) holder.count += 1;
  else watchers.set(worktreeId, { workerId: worktree.workerId, count: 1 });
  session.watchWorktree(worktreeId);
  return () => {
    const current = watchers.get(worktreeId);
    if (!current) return;
    current.count -= 1;
    if (current.count > 0) return;
    watchers.delete(worktreeId);
    sessions.get(current.workerId)?.unwatchWorktree(worktreeId);
  };
}

/** The provisional tail of one chat while it is subscribed. */
export function holdLocalChat(chatId: string, worktreeId: string): () => void {
  const holder = chatHolds.get(chatId);
  if (holder) holder.count += 1;
  else chatHolds.set(chatId, { worktreeId, count: 1 });
  sessionForWorktree(worktreeId)?.subscribeChat(chatId);
  return () => {
    const current = chatHolds.get(chatId);
    if (!current) return;
    current.count -= 1;
    if (current.count > 0) return;
    chatHolds.delete(chatId);
    const worktree = cachedWorktree(current.worktreeId);
    if (worktree?.workerId) sessions.get(worktree.workerId)?.unsubscribeChat(chatId);
    useChatStore.getState().dropProvisional(chatId);
  };
}

/** Keeps the worktree's LocalApi watch up while a workspace shows it. Keyed
 * by the worker's advertised LocalApi endpoint, so it starts when the worker
 * list says there is one (and follows a worker restart onto a new port). */
export function useLocalWorktree(worktreeId: string, worker: Pick<WorkerInfo, "status" | "capabilities"> | undefined): void {
  const endpoint = localApiDescriptor(worker)?.endpoint ?? null;
  useEffect(() => {
    if (!endpoint) return;
    return holdLocalWorktree(worktreeId);
  }, [worktreeId, endpoint]);
}
